import { useState } from "react";
import { Copy, Loader2, Shield, Pencil, Trash2, Plus, Info } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { useTranslation } from "@/shared/i18n";
import { useCreatorFinancialBankAccounts, useUpdateCreatorFinancialBankAccount } from "../model/hooks";
import type { CreatorBankAccount } from "../types";
import { EditBankAccountDialog, type BankAccountFormMode } from "../ui/EditBankAccountDialog";
import { MockBanner } from "../ui/MockBanner";
import { toast } from "@/shared/widgets/toast";

function getBankInitials(bank: string): string {
  const words = bank.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return bank.slice(0, 2).toUpperCase();
}

function getAccountFinal(account: string): string {
  const digits = account.replace(/\D/g, "");
  return digits.slice(-4) || "****";
}

/** Cor do quadrado do banco no card (design de referência) */
function getBankCardColor(bank: string): string {
  const name = bank.toLowerCase();
  if (name.includes("nubank") || name.includes("nu ")) return "bg-violet-600";
  if (name.includes("itaú") || name.includes("itau")) return "bg-orange-500";
  if (name.includes("brasil") || name.includes(" do b")) return "bg-blue-600";
  return "bg-[#FF2E6A]";
}

export function BankAccountsPage() {
  const { t } = useTranslation("creator-financial");
  const { bankAccounts, isLoading, refetch } = useCreatorFinancialBankAccounts();
  const { updateBankAccount, isLoading: updating } = useUpdateCreatorFinancialBankAccount();
  const [formMode, setFormMode] = useState<BankAccountFormMode>("edit");
  const [selectedAccount, setSelectedAccount] = useState<CreatorBankAccount | null>(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);

  const activeAccounts = bankAccounts.filter((a) => a.active);
  const otherAccounts = bankAccounts.filter((a) => !a.active);
  const defaultAccount = activeAccounts.find((a) => a.default) ?? activeAccounts[0];

  const handleCopyPix = (pix: string) => {
    navigator.clipboard.writeText(pix);
    toast.success(t("editBankAccount.copyPix"), "Copiado para a área de transferência.");
  };

  const handleSetAsDefault = async (account: CreatorBankAccount) => {
    await updateBankAccount({ id: account.id, payload: { default: true } });
    refetch();
  };

  const handleEdit = (account: CreatorBankAccount) => {
    setFormMode("edit");
    setSelectedAccount(account);
    setFormDialogOpen(true);
  };

  const handleCreate = () => {
    setFormMode("create");
    setSelectedAccount(null);
    setFormDialogOpen(true);
  };

  const handleFormSuccess = () => {
    refetch();
    setSelectedAccount(null);
  };

  const handleDelete = (_account: CreatorBankAccount) => {
    toast.info("Excluir conta", "Funcionalidade em breve.");
  };

  if (isLoading) {
    return (
      <div className="creator-financial-page flex items-center justify-center min-h-[320px]">
        <Loader2 className="h-8 w-8 animate-spin opacity-45" />
      </div>
    );
  }

  return (
    <div className="creator-financial-page -m-6 space-y-8 p-8 pb-8">
      <MockBanner />

      {/* Header: título + botão Adicionar Nova Conta */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="cf-page-title">{t("bankAccounts.title")}</h1>
          <p className="cf-page-subtitle">{t("bankAccounts.subtitle")}</p>
        </div>
        <Button className="shrink-0" onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          {t("bankAccounts.addAccount")}
        </Button>
      </div>

      {/* Contas Ativas */}
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-medium" style={{ color: "var(--cf-text)" }}>
            {t("bankAccounts.activeAccounts")}
          </h2>
          <p className="text-sm mt-0.5" style={{ color: "var(--cf-text-secondary)" }}>
            {t("bankAccounts.activeAccountsDesc")}
          </p>
        </div>
        {activeAccounts.length === 0 ? (
          <div className="cf-card border-dashed flex flex-col items-center justify-center py-12">
            <p style={{ color: "var(--cf-text-muted)" }}>{t("bankAccounts.empty")}</p>
            <p className="text-sm mt-1" style={{ color: "var(--cf-text-muted)" }}>
              {t("bankAccounts.emptyHint")}
            </p>
          </div>
        ) : (
          defaultAccount && (
            <>
              <div className="cf-card">
                <div className="flex flex-row flex-wrap items-start justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div
                      className={`cf-logo-bank ${getBankCardColor(defaultAccount.bank)}`}
                    >
                      {getBankInitials(defaultAccount.bank)}
                    </div>
                    <div>
                      <p className="cf-bank-name">{defaultAccount.bank}</p>
                      <div className="mt-1 flex flex-wrap gap-2">
                        <span className="cf-badge-pill cf-badge-ativa">
                          {t("bankAccounts.active")}
                        </span>
                        {defaultAccount.default && (
                          <span className="cf-badge-pill cf-badge-default">
                            {t("bankAccounts.default")}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm" style={{ color: "var(--cf-text-muted)" }}>
                        Conta Corrente • Pessoa Física
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(defaultAccount)}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      {t("bankAccounts.edit")}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(defaultAccount)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t("bankAccounts.delete")}
                    </Button>
                  </div>
                </div>
                <div className="mt-6 grid gap-6 sm:grid-cols-3">
                  <div>
                    <span className="cf-label">AGÊNCIA</span>
                    <p className="cf-value">{defaultAccount.branch}</p>
                  </div>
                  <div>
                    <span className="cf-label">NÚMERO DA CONTA</span>
                    <p className="cf-value">{defaultAccount.account}</p>
                  </div>
                  <div>
                    <span className="cf-label">CHAVE PIX</span>
                    <div className="cf-value flex items-center gap-2">
                      <span>{defaultAccount.pix}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => handleCopyPix(defaultAccount.pix)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="cf-info-bar flex items-center gap-2 mt-4">
                <Info className="h-4 w-4 shrink-0" style={{ color: "var(--cf-primary)" }} />
                {t("bankAccounts.depositNotice")}
              </div>
            </>
          )
        )}
      </section>

      {/* Outras Contas */}
      <section className="space-y-4">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-medium" style={{ color: "var(--cf-text)" }}>
            {t("bankAccounts.otherAccounts")}
          </h2>
          {otherAccounts.length > 0 && (
            <span
              className="text-xs uppercase tracking-wider"
              style={{ color: "var(--cf-text-muted)" }}
            >
              {t("bankAccounts.otherAccountsCount", { count: otherAccounts.length })}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {otherAccounts.map((account) => (
            <div key={account.id} className="cf-card flex flex-col">
              <div className="flex flex-row items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white text-sm font-semibold ${getBankCardColor(account.bank)}`}
                  >
                    {getBankInitials(account.bank)}
                  </div>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: "var(--cf-text)" }}>
                      {account.bank}
                    </p>
                    <p className="text-xs" style={{ color: "var(--cf-text-muted)" }}>
                      Final {getAccountFinal(account.account)}
                    </p>
                  </div>
                </div>
                <span className="cf-badge-pill cf-badge-inativa shrink-0">
                  {t("bankAccounts.inactive")}
                </span>
              </div>
              <div className="mt-4 pt-2">
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 font-medium"
                  disabled={updating}
                  onClick={() => handleSetAsDefault(account)}
                >
                  {t("bankAccounts.setAsDefault")}
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            className="cf-card-dashed flex flex-1 flex-col items-center justify-center py-10 h-auto border-dashed"
            onClick={handleCreate}
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-dashed border-current opacity-60">
              <Plus className="h-7 w-7" />
            </div>
            <p className="mt-3 text-sm font-medium text-muted-foreground">
              {t("bankAccounts.linkAccount")}
            </p>
          </Button>
        </div>
      </section>

      {/* Segurança de Dados */}
      <div className="cf-security-card flex items-start gap-4">
        <Shield className="h-10 w-10 shrink-0" style={{ color: "var(--cf-primary)" }} />
        <div>
          <h3 className="cf-security-title">{t("bankAccounts.securityTitle")}</h3>
          <p className="cf-security-text">{t("bankAccounts.securityDesc")}</p>
        </div>
      </div>

      <EditBankAccountDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        mode={formMode}
        account={selectedAccount}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
}
